import { useState, type ReactNode } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { unfold } from '../utils/motion';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, usePropertyAddress, usePropertyAddressUrl, EditableCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { addressLink, cap, clean, hoursRows, num, telHref, titleCase, toParagraph, word, type OfficeHour } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { useSiteFlags } from '../utils/site';

const DAY: Record<string, string> = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };

/** "Monday through Friday, 9 am – 6 pm. Saturday, 10 am – 5 pm. Sunday, Closed." */
function hoursSentence(hours: OfficeHour[]): string {
  return hoursRows(hours, false)
    .map(([d, t]) => `${d.split(' \u2013 ').map((x) => DAY[x] || x).join(' through ')}, ${t}.`)
    .join(' ');
}

const list = (xs: string[]) => (xs.length < 3 ? xs.join(' and ') : `${xs.slice(0, -1).join(', ')}, and ${xs[xs.length - 1]}`);

/** "Three- and four-bedroom layouts, including the Brunswick, Guilford, and Lancaster." */
function homesSentence(p: Record<string, unknown>): string {
  const plans = (p.floorplans as Array<{ floorplan_name?: string; floorplan_beds_min?: string }>) || [];
  const beds = [...new Set(plans.map((f) => num(f.floorplan_beds_min)).filter((b): b is number => b != null))].sort((a, b) => a - b);
  const names = plans.map((f) => titleCase(clean(f.floorplan_name))).filter(Boolean).slice(0, 4);
  const bedWords = beds.length > 1 ? `${beds.slice(0, -1).map((b) => `${word(b)}-`).join(', ')} and ${word(beds[beds.length - 1])}-bedroom` : beds.length ? `${word(beds[0])}-bedroom` : '';
  if (!bedWords && !names.length) return '';
  return `${cap(bedWords ? `${bedWords} layouts` : 'Every layout')}${names.length ? `, including the ${list(names)}` : ''}.`;
}

const Plus = ({ open }: { open: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path d={open ? 'M3 8h10' : 'M3 8h10M8 3v10'} />
  </svg>
);

const ANSWER =
  'max-w-[680px] text-base leading-[26px] [&_a]:font-medium [&_a]:text-[--q-primary-on-paper] [&_a]:underline-offset-[3px] [&_p]:m-0 max-[767px]:text-[15px] max-[767px]:leading-[22px]';

function Item({ q, children }: { q: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="border-t border-t-[--q-border] last:border-b last:border-b-[--q-border]">
      <Collapsible.Trigger className="flex w-full items-center gap-6 px-0 py-[22px] text-left text-inherit aria-expanded:pb-3 max-[767px]:gap-3 max-[767px]:py-4 max-[767px]:aria-expanded:pb-2">
        <span className="flex-auto font-heading text-[24px] leading-8 [&_p]:m-0 max-[767px]:text-[20px] max-[767px]:leading-[26px]">{q}</span>
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[--q-border] text-[--q-primary-on-paper] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-width:1.6]">
          <Plus open={open} />
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content className={unfold}>
        <div className="pb-6 max-[767px]:pb-[18px]">{children}</div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

/** Before you arrive (Paper 11): hours, the address, how you get in, who to call, which homes. */
export function TourQuestions() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const hours = useCollectionBinding<OfficeHour>('tourquestions.hours', { collection: 'office_hours' });
  const when = hoursSentence(hours);
  const address =
    clean(usePropertyAddress()) ||
    [clean(p.property_address_1), [clean(p.property_address_city), clean(p.property_address_state)].filter(Boolean).join(', '), clean(p.property_address_zip)].filter(Boolean).join(' ');
  const addressUrl = usePropertyAddressUrl();
  const tel = telHref(flags.phone);
  const homes = homesSentence(p);

  return (
    <section className="section section--surface tqs flex items-start gap-20 max-[1199px]:flex-col max-[1199px]:gap-10 max-[767px]:gap-7">
      <div className="flex flex-[0_0_360px] flex-col gap-4 [&_.h2_p]:m-0 max-[1199px]:basis-auto" data-reveal>
        <RichTextContent path="tourquestions.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="tourquestions.title" as="h2" className="h2" />
        <RichTextContent
          path="tourquestions.lead"
          as="p"
          className="m-0 text-base leading-6 text-[--q-muted] [&_p]:m-0 max-[767px]:text-[15px] max-[767px]:leading-[22px]"
        />
      </div>
      <div className="min-w-0 max-w-[808px] flex-auto max-[1199px]:w-full max-[1199px]:max-w-none">
        <Item q={<RichTextContent path="tourquestions.when_q" as="span" />}>
          <EditableCollectionBinding bindingPath="tourquestions.hours" label="Office Hours">
            <RichTextContent
              path="tourquestions.when_a"
              as="div"
              className={ANSWER}
              defaultValue={toParagraph(when ? `${when} The scheduler shows the times you can book.` : 'The scheduler shows the times you can book.')}
            />
          </EditableCollectionBinding>
        </Item>
        {address && (
          <Item q={<RichTextContent path="tourquestions.where_q" as="span" />}>
            <RichTextContent path="tourquestions.where_a" as="div" className={ANSWER} defaultValue={addressLink(address, addressUrl) ?? toParagraph(address)} />
          </Item>
        )}
        <Item q={<RichTextContent path="tourquestions.how_q" as="span" />}>
          <RichTextContent path="tourquestions.how_a" as="div" className={ANSWER} />
        </Item>
        {tel && (
          <Item q={<RichTextContent path="tourquestions.who_q" as="span" />}>
            <RichTextContent path="tourquestions.who_a" as="div" className={ANSWER} defaultValue={`<p><a class="dniphone dniphonehref" href="${tel}">${flags.phone}</a></p>`} />
          </Item>
        )}
        {homes && (
          <Item q={<RichTextContent path="tourquestions.homes_q" as="span" />}>
            <RichTextContent path="tourquestions.homes_a" as="div" className={ANSWER} defaultValue={toParagraph(homes)} />
          </Item>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TourQuestions',
  displayName: 'Before You Arrive',
  tokens: {
    'tourquestions.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Before you arrive</p>' },
    'tourquestions.title': { label: 'Title', type: 'richtext', defaultValue: '<p>A few things worth knowing.</p>' },
    'tourquestions.lead': { label: 'Lead', type: 'richtext', defaultValue: '<p>Hours, the address, and how you get in.</p>' },
    'tourquestions.when_q': { label: 'When · Question', type: 'richtext', defaultValue: '<p>When can I visit?</p>' },
    'tourquestions.when_a': {
      label: 'When · Answer',
      description: 'Until edited, gives the office hours.',
      type: 'richtext',
      defaultValue: '<p>The scheduler shows the times you can book.</p>',
    },
    'tourquestions.where_q': { label: 'Where · Question', type: 'richtext', defaultValue: '<p>Where do I go?</p>' },
    'tourquestions.where_a': {
      label: 'Where · Answer',
      description: 'Until edited, the address as a Google Maps link.',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_full_address"></span></p>',
    },
    'tourquestions.how_q': { label: 'Access · Question', type: 'richtext', defaultValue: '<p>How do I get in?</p>' },
    'tourquestions.how_a': {
      label: 'Access · Answer',
      type: 'richtext',
      defaultValue: '<p>Choose a time in the scheduler. Your access details are sent before you arrive.</p>',
    },
    'tourquestions.who_q': { label: 'Call · Question', type: 'richtext', defaultValue: '<p>Who can I call?</p>' },
    'tourquestions.who_a': {
      label: 'Call · Answer',
      description: 'Until edited, the leasing phone number as a call link.',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_phone"></span></p>',
    },
    'tourquestions.homes_q': { label: 'Homes · Question', type: 'richtext', defaultValue: '<p>Which homes can I see?</p>' },
    'tourquestions.homes_a': {
      label: 'Homes · Answer',
      description: 'Until edited, names the bedroom counts and the floor plans.',
      type: 'richtext',
      defaultValue: '<p>Every available floor plan.</p>',
    },
  },
  collectionBindings: {
    'tourquestions.hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(TourQuestions as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourQuestions;
